import { Flags } from '@core/context/FeatureFlagsContext';
import { type DCS } from '@core/reducers/dcsSlice';
import { EduRequestV2, type QualificationResults } from '@core/ts/leadDelivery';
import { generateEduRequestV2 } from '@core/utils/requests/EduRequestV2';

import { type InjectionKey } from '../getQuestionInjectionSchema';

export type GCPParams = {
  dcs: DCS;
  ipInferredPostalCode: string;
  publisher: string;
};

const qualificationsMap = {
  isGradDegree: 'E0C4',
  isEabGradBisCategory: 'E3E1',
  hasEABGradBisZipcode: 'B7B6',
  isEABEducationCategory: 'E8F4',
  hasEABGradEduZipcode: 'D1A0',
  isAssociatesDegree: 'B1C0',
  isEmccSubject: 'F0E5',
  hasEMCCZipcode: 'F7E6',
  hasEABGradHealthZipcode: 'E3C1',
  isEABHealthcareCategory: 'F7A4',
  isUndergradDegree: 'A4B7',
  hasEABUndergradZipcode: 'C1E1',
  hasValidOnOPublisher: 'C3B3',
  hasGivenEABConsent: 'E0E5',
  hasGivenEMCCConsent: 'D2B3',
  isEabNursingSubject: 'C6D5',
  hasEABNursingZipcode: 'C6D1',
  isNotEABNursingSubject: 'D6D8',
  isEABGradStemSubject: 'B3F0',
  hasEABGradStemZipcode: 'E2E7',
};

const {
  isGradDegree,
  isEabGradBisCategory,
  hasEABGradBisZipcode,
  isEABEducationCategory,
  hasEABGradEduZipcode,
  isAssociatesDegree,
  isEmccSubject,
  hasEMCCZipcode,
  isEABHealthcareCategory,
  hasEABGradHealthZipcode,
  isUndergradDegree,
  hasEABUndergradZipcode,
  hasValidOnOPublisher,
  hasGivenEABConsent,
  hasGivenEMCCConsent,
  isEabNursingSubject,
  hasEABNursingZipcode,
  isNotEABNursingSubject,
  isEABGradStemSubject,
  hasEABGradStemZipcode,
} = qualificationsMap;

// EAB Grad Tranches
// const isGradBusinessEligible = [isGradDegree, isEabGradBisCategory, hasEABGradBisZipcode];
// The line above was the original code.  For July2024, removed the zip qualification for EAB Grad Business
const isGradBusinessEligible = [isGradDegree, isEabGradBisCategory, hasValidOnOPublisher];
const isGradEducationEligible = [isGradDegree, isEABEducationCategory, hasEABGradEduZipcode, hasValidOnOPublisher];
const isGradHealthEligible = [isGradDegree, isEABHealthcareCategory, hasEABGradHealthZipcode, hasValidOnOPublisher];
const isGradNursingEligible = [isGradDegree, isEabNursingSubject, hasEABNursingZipcode, hasValidOnOPublisher];
const isGradStemEligible = [isGradDegree, isEABGradStemSubject, hasEABGradStemZipcode, hasValidOnOPublisher];
const hasGivenConsent = [hasGivenEABConsent];
// EAB Undergrad Tranch
const isUndergradEligible = [isUndergradDegree, hasEABUndergradZipcode, hasValidOnOPublisher, isNotEABNursingSubject];
const isUndergradNursingEligible = [isUndergradDegree, isEabNursingSubject, hasEABNursingZipcode, hasValidOnOPublisher];

// EMCC Tranch
const isEmccEligible = [isAssociatesDegree, isEmccSubject, hasEMCCZipcode, hasValidOnOPublisher];
const hasConsentedEMCC = [hasGivenEMCCConsent];

// Map of partner keys to their respective qualification tranches
const PartnerKeyToQualificationsMap: Record<string, string[][]> = {
  EAB_GRAD: [
    isGradBusinessEligible,
    isGradEducationEligible,
    isGradHealthEligible,
    isGradNursingEligible,
    isGradStemEligible,
    hasGivenConsent,
  ],
  EMCC: [isEmccEligible, hasConsentedEMCC],
  EAB_UNDERGRAD: [isUndergradEligible, isUndergradNursingEligible, hasGivenConsent],
};
// Map of partner keys to their respective feature flags
const PartnerKeyToFeatureFlagMap: Record<string, Array<keyof Flags>> = {
  EAB_GRAD: ['useEAB', 'testEAB'],
  EAB_UNDERGRAD: ['useEAB', 'testEAB'],
  EMCC: ['useEMCC', 'testEMCC'],
};

/**
 *
 * @param qualified QualificationResults['qualified']
 * @returns returns all the slugs that the user is qualified for
 */
export const getQualifiedPartnerKeys = (qualified: QualificationResults['qualified'], flags: Flags): InjectionKey[] =>
  Object.entries(PartnerKeyToQualificationsMap).reduce((injectionKeys: InjectionKey[], [slug, tranches]) => {
    // search through each partner and their tranches to verify eligibility
    const isQualified = tranches.some((qualificationIds) =>
      qualificationIds.every((id) => qualified.some((qualification) => qualification.id === id))
    );
    const hasFeatureFlagActive = PartnerKeyToFeatureFlagMap[slug].some((flag: keyof Flags) => flags[flag]);

    if (isQualified && hasFeatureFlagActive) return [...injectionKeys, slug] as InjectionKey[];
    return injectionKeys;
  }, []);

/**
 *
 * @param param0.dcs DCS
 * @param param0.ipInferredPostalCode string
 * @returns generate the payload for the qualify request
 */
export const generateQualifyPayload = ({ dcs, ipInferredPostalCode, publisher }: GCPParams): EduRequestV2 =>
  generateEduRequestV2({
    lead: {
      location: {
        ipInferredPostalCode,
      },
    },
    programContext: {
      degree: {
        name: dcs.dcsDegrees[0],
      },
      category: {
        name: dcs.dcsCategories[0],
      },
      subject: {
        name: dcs.dcsSubjects[0],
      },
    },
    trackingContext: {
      publisher: publisher || window.HE?.publisher?.name || window.location.host,
    },
  });
